import { CheckCircleIcon, XCircleIcon } from "@";
import type { RefObject } from "react";
import { ActionButton } from "./atoms.tsx";

interface ActionBarProps {
  canAccept: boolean;
  canReject: boolean;
  isSubmitting: boolean;
  rejectMode: boolean;
  rejectComment: string;
  rejectError: string;
  setRejectComment: (v: string) => void;
  setRejectError: (v: string) => void;
  enterRejectMode: () => void;
  cancelRejectMode: () => void;
  handleAccept: () => void;
  handleRejectConfirm: () => void;
  textareaRef: RefObject<HTMLTextAreaElement>;
}

export const ActionBar = (props: ActionBarProps) => (
  <footer
    onClick={(e) => e.stopPropagation()}
    className="border-t border-white/10 bg-black/40 px-4 py-4 backdrop-blur-md md:px-6"
  >
    {props.rejectMode ? (
      <RejectCommentBar {...props} />
    ) : (
      <DefaultBar {...props} />
    )}
  </footer>
);

const DefaultBar = ({
  canAccept,
  canReject,
  isSubmitting,
  enterRejectMode,
  handleAccept,
}: ActionBarProps) => (
  <div className="mx-auto flex max-w-3xl items-center justify-center gap-3">
    <ActionButton
      tone="reject"
      disabled={!canReject || isSubmitting}
      onClick={enterRejectMode}
      icon={<XCircleIcon className="h-6 w-6" />}
      label="رفض"
      hint={!canReject ? "متاح فقط لطلبات بانتظار التحقق" : undefined}
    />
    <ActionButton
      tone="accept"
      disabled={!canAccept || isSubmitting}
      onClick={handleAccept}
      icon={<CheckCircleIcon className="h-6 w-6" />}
      label="تأكيد"
      loading={isSubmitting}
      hint={!canAccept ? "غير قابل للتأكيد بهذه الحالة" : undefined}
    />
  </div>
);

const RejectCommentBar = ({
  isSubmitting,
  rejectComment,
  rejectError,
  setRejectComment,
  setRejectError,
  cancelRejectMode,
  handleRejectConfirm,
  textareaRef,
}: ActionBarProps) => (
  <div className="mx-auto flex max-w-3xl flex-col gap-2">
    <label className="flex items-center gap-2 text-sm text-white/90">
      <span className="font-medium">سبب الرفض</span>
      <span className="text-red-400">*</span>
    </label>
    <textarea
      ref={textareaRef}
      value={rejectComment}
      onChange={(e) => {
        setRejectComment(e.target.value);
        if (rejectError) setRejectError("");
      }}
      rows={3}
      placeholder="اكتب سبب الرفض..."
      className="w-full resize-none rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/40 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-400/20"
    />
    {rejectError && <p className="text-xs text-red-400">{rejectError}</p>}
    <div className="flex items-center justify-between gap-3 pt-1">
      <div className="ms-auto flex gap-2">
        <button
          type="button"
          onClick={cancelRejectMode}
          disabled={isSubmitting}
          className="rounded-2xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/90 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          إلغاء
        </button>
        <button
          type="button"
          onClick={handleRejectConfirm}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          )}
          تأكيد الرفض
        </button>
      </div>
    </div>
  </div>
);
